import { useSyncExternalStore } from "react";
import type { FileEntry, FileKind, FileOwnerKind } from "./file-index";

export const FILE_MOCK_CASES = [
  { id: "many", label: "많이" },
  { id: "few", label: "적게" },
  { id: "byPlan", label: "플랜별" },
  { id: "empty", label: "없음" },
] as const;

export type FileMockCase = "live" | (typeof FILE_MOCK_CASES)[number]["id"];

export interface FileMockPlan {
  id: string;
  name: string;
}

const EXTRA_PLANS: FileMockPlan[] = [
  { id: "mock-plan-launch", name: "제품 출시" },
  { id: "mock-plan-content", name: "콘텐츠 운영" },
  { id: "mock-plan-partner", name: "파트너 캠페인" },
];

const AUTHORS = ["김PM", "이디자인", "박엔진", "정프론트", "송DB", ""];
const OWNER_TITLES = ["페르소나 워크숍", "매체 견적 검토", "동의 문구", "실험 설계", "세그먼트 정의"];

const SOURCE_CYCLE: Array<{ ownerKind: FileOwnerKind; comment: boolean }> = [
  { ownerKind: "task", comment: false },
  { ownerKind: "record", comment: false },
  { ownerKind: "document", comment: false },
  { ownerKind: "task", comment: true },
  { ownerKind: "record", comment: true },
  { ownerKind: "document", comment: true },
];

const KIND_CYCLE: FileKind[] = ["image", "file", "link"];

const STEMS: Record<FileKind, string[]> = {
  image: ["키비주얼.png", "현장 사진.png", "배너 시안.png", "세그먼트 차트.png"],
  file: ["캠페인 브리프.pdf", "매체 견적.xlsx", "동의 문구.docx", "실험 결과.pdf"],
  link: ["브랜드 가이드", "개인정보 처리방침", "광고 정책", "참고 레퍼런스"],
};

const LONG_TITLE = "2026년 4분기 그로스 캠페인 키비주얼 최종 확정본 모바일 홈 상단과 앱 스플래시.png";

const MOCK_CASE_KEY = "file-mock-case";

function readStoredCase(): FileMockCase {
  try {
    const raw = sessionStorage.getItem(MOCK_CASE_KEY);
    if (raw === "many" || raw === "few" || raw === "byPlan" || raw === "empty" || raw === "live") return raw;
  } catch {
    // 이 환경에는 sessionStorage가 없다.
  }
  return "live";
}

let current: FileMockCase = readStoredCase();
const listeners = new Set<() => void>();

export function getFileMockCase(): FileMockCase {
  return current;
}

export function setFileMockCase(next: FileMockCase) {
  if (current === next) return;
  current = next;
  try {
    sessionStorage.setItem(MOCK_CASE_KEY, next);
  } catch {
    // 저장하지 못해도 이번 화면에는 적용한다.
  }
  for (const listener of listeners) listener();
}

export function useFileMockCase(): FileMockCase {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    getFileMockCase,
    getFileMockCase,
  );
}

export function isFileMockEntry(entry: FileEntry): boolean {
  return entry.attachment.id.startsWith("mock:");
}

export function fileMockEntries(
  scenario: Exclude<FileMockCase, "live">,
  options: { plans: FileMockPlan[]; focusPlanId?: string },
): FileEntry[] {
  if (scenario === "empty") return [];
  if (scenario === "few") return build(3, [namedPlan(options.plans, options.focusPlanId)], "few");
  if (scenario === "byPlan") {
    const plans = spreadPlans(options.plans, options.focusPlanId);
    const counts = [14, 6, 4, 2];
    const out: FileEntry[] = [];
    plans.forEach((plan, index) => {
      const count = counts[index] ?? 2;
      const kind: FileKind | "mix" = index === 1 ? "image" : index === 2 ? "file" : index === 3 ? "link" : "mix";
      out.push(...build(count, [plan], "byPlan", out.length, kind));
    });
    return out;
  }
  const focus = options.focusPlanId ? [namedPlan(options.plans, options.focusPlanId)] : resolvePlans(options.plans, 1);
  return build(48, focus, "many");
}

function namedPlan(plans: FileMockPlan[], focusPlanId?: string): FileMockPlan {
  const found = plans.find((plan) => plan.id === focusPlanId);
  if (found) return { id: found.id, name: found.name || "이름 없는 플랜" };
  if (focusPlanId) return { id: focusPlanId, name: "이 플랜" };
  return resolvePlans(plans, 1)[0]!;
}

function resolvePlans(plans: FileMockPlan[], min: number): FileMockPlan[] {
  const out: FileMockPlan[] = [];
  const seen = new Set<string>();
  const push = (plan: FileMockPlan) => {
    if (!plan.id || seen.has(plan.id)) return;
    seen.add(plan.id);
    out.push({ id: plan.id, name: plan.name || "이름 없는 플랜" });
  };
  for (const plan of plans) push(plan);
  for (const plan of EXTRA_PLANS) {
    if (out.length >= min) break;
    push(plan);
  }
  if (out.length === 0) out.push({ id: "mock-plan", name: "이름 없는 플랜" });
  return out;
}

function spreadPlans(plans: FileMockPlan[], focusPlanId?: string): FileMockPlan[] {
  const focus = namedPlan(plans, focusPlanId);
  const out: FileMockPlan[] = [];
  const seen = new Set<string>();
  for (const plan of [focus, ...plans, ...EXTRA_PLANS]) {
    if (!plan.id || seen.has(plan.id)) continue;
    seen.add(plan.id);
    out.push({ id: plan.id, name: plan.name || "이름 없는 플랜" });
    if (out.length === 4) break;
  }
  return out;
}

function build(count: number, plans: FileMockPlan[], scenario: string, offset = 0, forced: FileKind | "mix" = "mix"): FileEntry[] {
  const out: FileEntry[] = [];
  for (let n = 0; n < count; n += 1) {
    const index = offset + n;
    const plan = plans[n % plans.length]!;
    const kind = forced === "mix" ? KIND_CYCLE[index % KIND_CYCLE.length]! : forced;
    const source = SOURCE_CYCLE[index % SOURCE_CYCLE.length]!;
    const stem = STEMS[kind][index % STEMS[kind].length]!;
    const title = index === 6 && kind === "image" ? LONG_TITLE : count > STEMS[kind].length ? `${stem.replace(/\.[^.]+$/, "")} ${index + 1}${ext(stem)}` : stem;
    const day = String(20 - (index % 20)).padStart(2, "0");
    const hour = String(index % 9).padStart(2, "0");
    const addedAt = `2026-10-${day}T${hour}:00:00.000Z`;
    const author = AUTHORS[index % AUTHORS.length]!;
    const comment = source.comment;
    out.push({
      key: `mock:${scenario}:${index}`,
      attachment: {
        id: `mock:${scenario}:${index}`,
        title,
        url: kind === "link" ? `https://example.com/files/${index}` : kind === "image" ? thumb(title, index) : `data:application/octet-stream,${index}`,
        addedAt,
        addedBy: author,
        kind,
        size: kind === "link" ? undefined : 12_000 + index * 17_000,
        mimeType: kind === "image" ? "image/svg+xml" : undefined,
      },
      projectId: plan.id,
      projectName: plan.name,
      ownerKind: source.ownerKind,
      ownerId: `mock-owner-${index}`,
      ownerTitle: OWNER_TITLES[index % OWNER_TITLES.length]!,
      ownerCode: source.ownerKind === "record" ? `ISS-${(index % 9) + 1}` : source.ownerKind === "document" ? `DOC-${(index % 5) + 1}` : undefined,
      commentId: comment ? `mock-comment-${index}` : undefined,
      addedAt,
      addedBy: author,
      kind,
    });
  }
  return out;
}

function ext(stem: string): string {
  const dot = stem.lastIndexOf(".");
  return dot === -1 ? "" : stem.slice(dot);
}

function thumb(label: string, index: number): string {
  const hue = (index * 47) % 360;
  const text = label.length > 18 ? `${label.slice(0, 18)}…` : label;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="480" height="360"><rect width="100%" height="100%" fill="hsl(${hue} 42% 42%)"/><text x="24" y="56" fill="white" font-family="sans-serif" font-size="28">${escapeXml(text)}</text></svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

function escapeXml(value: string): string {
  return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}
